import { createStyles, makeStyles, Menu, MenuItem } from '@material-ui/core';
import clsx from 'clsx';
import { StringMap, TOptions } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppMeta } from 'src/core/provider/AppMeta';

const useStyles = makeStyles(theme =>
    createStyles({
        root: {
            '&:hover': {
                outlineWidth: '1px',
                outlineStyle: 'dotted',
                outlineColor: theme.palette.info.dark,
            },
        },
        highlight: {
            outlineWidth: '1px',
            outlineStyle: 'dotted',
            outlineColor: theme.palette.info.dark,
            backgroundColor: theme.palette.info.light,
        },
    })
);

interface TProps<TInterpolationMap extends object = StringMap> {
    options?: TOptions<TInterpolationMap> | string;
    k: string;
}

const T: React.FC<TProps> = ({ k, options }) => {
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const { translationMode, translationFocusKey, setTranslationFocus } = useAppMeta();

    const [contextMenu, setContextMenu] = React.useState<{
        mouseX: number;
        mouseY: number;
    } | null>(null);

    // If translation mode is false we simply return the translated string
    if (!translationMode) return t(k, options);

    const keyHasNs = /\w+:\w+.*\w+/.test(k);

    if (!keyHasNs) k = `${i18n.options.defaultNS}:${k}`;

    const isCurrentFocus = translationFocusKey === k;

    const handleContextMenu = (event: React.MouseEvent) => {
        event.preventDefault();
        setContextMenu(
            contextMenu === null
                ? {
                      mouseX: event.clientX - 2,
                      mouseY: event.clientY - 4,
                  }
                : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
                  // Other native context menus might behave different.
                  // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
                  null
        );
    };

    const handleClose = () => {
        setContextMenu(null);
    };

    const handleTranslateItem = () => {
        setTranslationFocus(k);
        handleClose();
    };

    return (
        <>
            <span
                onContextMenu={handleContextMenu}
                style={{ cursor: 'context-menu' }}
                className={clsx(classes.root, {
                    [classes.highlight]: isCurrentFocus,
                })}>
                {t(k)}
            </span>
            <Menu
                keepMounted
                open={contextMenu !== null}
                onClose={handleClose}
                anchorReference="anchorPosition"
                anchorPosition={
                    contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined
                }>
                <MenuItem onClick={handleTranslateItem}>{t('contentmanager:translate_item')}</MenuItem>
            </Menu>
        </>
    );
};

export default T;
